import _createForOfIteratorHelper from '@babel/runtime/helpers/esm/createForOfIteratorHelper';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import { extensionDecorator, MarkExtension, ExtensionTag, isElementDomNode, omitExtraAttributes, getSelectedWord, isTextSelection, isSelectionEmpty, isAllSelection, isMarkActive, updateMark, removeMark, markPasteRule, getMatchString, ExtensionPriority, getMarkRange, LEAF_NODE_REPLACING_CHARACTER, preserveSelection, range } from '@remirror/core';
import { TextSelection } from '@remirror/pm/state';
import { isInvalidSplitReason, isRemovedReason } from '@remirror/pm/suggest';

var _dec, _class, _temp;
var UPDATE_LINK = 'updateLink';
/**
 * Can be an empty string which sets url's to '//google.com'.
 */

var LinkExtension = (_dec = /*#__PURE__*/extensionDecorator({
  defaultOptions: {
    autoLink: false,
    defaultProtocol: '',
    selectTextOnClick: false,
    openLinkOnClick: false,
    autoLinkRegex: /(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[\da-z]+([.-][\da-z]+)*\.[a-z]{2,5}(:\d{1,5})?(\/\S*)?/
  },
  staticKeys: ['autoLinkRegex'],
  handlerKeyOptions: {
    onClick: {
      earlyReturnValue: true
    }
  },
  handlerKeys: ['onActivateLink', 'onUpdateLink', 'onClick']
}), /*#__PURE__*/_dec(_class = (_temp = class LinkExtension extends MarkExtension {
  constructor() {
    super(...arguments);

    _defineProperty(this, "tags", [ExtensionTag.Link]);
  }

  get name() {
    return 'link';
  }

  createMarkSpec(extra) {
    var AUTO_ATTRIBUTE = 'data-link-auto';
    return {
      attrs: _objectSpread(_objectSpread({}, extra.defaults()), {}, {
        href: {},
        auto: {
          default: false
        }
      }),
      inclusive: false,
      parseDOM: [{
        tag: 'a[href]',
        getAttrs: node => {
          var _node$textContent;

          if (!isElementDomNode(node)) {
            return false;
          }

          var href = node.getAttribute('href');
          var auto = node.hasAttribute(AUTO_ATTRIBUTE) || this.options.autoLinkRegex.test((_node$textContent = node.textContent) !== null && _node$textContent !== void 0 ? _node$textContent : '');
          return _objectSpread(_objectSpread({}, extra.parse(node)), {}, {
            href,
            auto
          });
        }
      }],
      toDOM: node => {
        var _omitExtraAttributes = omitExtraAttributes(node.attrs, extra),
            _ = _omitExtraAttributes.auto,
            rest = _objectWithoutProperties(_omitExtraAttributes, ["auto"]);

        var auto = node.attrs.auto ? {
          [AUTO_ATTRIBUTE]: ''
        } : {};
        var rel = 'noopener noreferrer nofollow';

        var attrs = _objectSpread(_objectSpread(_objectSpread({}, extra.dom(node)), rest), {}, {
          rel
        }, auto);

        return ['a', attrs, 0];
      }
    };
  }

  onSetOptions(options) {
    if (options.changes.autoLink.changed) {
      if (options.changes.autoLink.value === true) {
        this.store.addSuggester(this.createSuggesters()[0]);
      }

      if (options.changes.autoLink.value === false) {
        this.store.removeSuggester(this.name);
      }
    }
  }

  createKeymap() {
    return {
      'Mod-k': (_ref) => {
        var state = _ref.state,
            dispatch = _ref.dispatch;
        // if the selection is empty, expand it
        var range = state.selection.empty ? getSelectedWord(state) : state.selection;

        if (!range) {
          return false;
        }

        var from = range.from,
            to = range.to;
        var tr = state.tr.setSelection(TextSelection.create(state.doc, from, to));

        if (dispatch) {
          dispatch(tr);
        }

        this.options.onActivateLink(tr.doc.textBetween(from, to));
        return true;
      }
    };
  }

  createCommands() {
    return {
      /**
       * Create or update the link if it doesn't currently exist at the current
       * selection or provided range.
       */
      updateLink: (attrs, range) => {
        return parameter => {
          var tr = parameter.tr;
          var selection = tr.selection;
          var selectionIsValid = isTextSelection(selection) && !isSelectionEmpty(tr.selection) || isAllSelection(selection) || isMarkActive({
            trState: tr,
            type: this.type
          });

          if (!selectionIsValid && !range) {
            return false;
          }

          tr.setMeta(this.name, {
            command: UPDATE_LINK,
            attrs,
            range
          });
          return updateMark({
            type: this.type,
            attrs,
            range
          })(parameter);
        };
      },

      /**
       * Remove the link at the current selection
       */
      removeLink: range => {
        return parameter => {
          var tr = parameter.tr;

          if (!isMarkActive(_objectSpread({
            trState: tr,
            type: this.type
          }, range))) {
            return false;
          }

          return removeMark({
            type: this.type,
            expand: true,
            range
          })(parameter);
        };
      }
    };
  }
  /**
   * Create the paste rules that can transform a pasted link in the document.
   */


  createPasteRules() {
    if (this.options.autoLink) {
      return [];
    }

    return [markPasteRule({
      regexp: /https?:\/\/(www\.)?[\w#%+.:=@~-]{2,256}\.[a-z]{2,6}\b([\w#%&+./:=?@~-]*)/gi,
      type: this.type,
      getAttributes: url => ({
        href: getMatchString(url),
        auto: true
      })
    })];
  }

  createSuggesters() {
    if (!this.options.autoLink) {
      return [];
    } // Keep track of this to prevent multiple updates which prevent history from
    // working


    var cachedRange;
    var suggester = {
      name: this.name,
      matchOffset: 0,
      supportedCharacters: /$/,
      char: this.options.autoLinkRegex,
      priority: ExtensionPriority.Lowest,
      caseInsensitive: true,
      disableDecorations: true,
      appendTransaction: true,
      checkNextValidSelection: ($pos, tr) => {
        var _cachedRange;

        var range = getMarkRange($pos, this.type);

        if (!range || ((_cachedRange = cachedRange) === null || _cachedRange === void 0 ? void 0 : _cachedRange.from) === $pos.pos && cachedRange.to === range.to) {
          return;
        }

        if (!range.mark.attrs.auto) {
          return;
        }

        var mark = range.mark,
            from = range.from,
            to = range.to;
        var text = $pos.doc.textBetween(from, to, LEAF_NODE_REPLACING_CHARACTER, ' ');
        var href = extractHref(text, this.options.defaultProtocol);

        if (from === range.from && to === range.to && mark.attrs.href === href) {
          return;
        } // The helpers to use here.


        var getSuggestMethods = this.store.helpers.getSuggestMethods; // Using the chainable commands so that the selection can be preserved
        // for the update.

        var _this$store$chain = this.store.chain,
            updateLink = _this$store$chain.updateLink,
            removeLink = _this$store$chain.removeLink,
            custom = _this$store$chain.custom,
            restore = _this$store$chain.restore;

        var _getSuggestMethods = getSuggestMethods(),
            findMatchAtPosition = _getSuggestMethods.findMatchAtPosition;

        var selection = tr.selection; // Keep track of the last removal.

        cachedRange = {
          from: $pos.pos,
          to
        }; // Set the transaction to update for the chainable commands.

        custom(tr); // Remove the link

        removeLink(cachedRange); // Make sure the selection gets preserved otherwise the cursor jumps
        // around.

        preserveSelection(selection, tr); // Check for active matches from the provided $pos

        var match = findMatchAtPosition($pos, this.name);

        if (match) {
          var _range = match.range,
              _text = match.text;
          updateLink({
            href: extractHref(_text.full, this.options.defaultProtocol),
            auto: true
          }, _range);
        } // Make sure to restore the shared transaction to it's default value.


        restore();
      },
      onChange: (details, tr) => {
        var selection = tr.selection;
        var text = details.text,
            range$1 = details.range,
            exitReason = details.exitReason,
            setMarkRemoved = details.setMarkRemoved;
        var href = extractHref(text.full, this.options.defaultProtocol); // Using the chainable commands so that the selection can be preserved
        // for the update.

        var _this$store$chain2 = this.store.chain,
            updateLink = _this$store$chain2.updateLink,
            removeLink = _this$store$chain2.removeLink,
            custom = _this$store$chain2.custom,
            restore = _this$store$chain2.restore;
        var getSuggestMethods = this.store.helpers.getSuggestMethods;

        var _getSuggestMethods2 = getSuggestMethods(),
            findMatchAtPosition = _getSuggestMethods2.findMatchAtPosition;
        /** Remove the link at the provided range. */


        var remove = () => {
          var _markRange;

          // Call the command to use a custom transaction rather than the current.
          custom(tr);
          var markRange;

          var _iterator = _createForOfIteratorHelper(range(range$1.from, range$1.to)),
              _step;

          try {
            for (_iterator.s(); !(_step = _iterator.n()).done;) {
              var pos = _step.value;
              markRange = getMarkRange(tr.doc.resolve(pos), this.type);

              if (markRange) {
                break;
              }
            }
          } catch (err) {
            _iterator.e(err);
          } finally {
            _iterator.f();
          }

          removeLink((_markRange = markRange) !== null && _markRange !== void 0 ? _markRange : range$1); // The mark range for the position after the matched text. If this
          // exists it should be removed to handle cleanup properly.

          var afterMarkRange;

          var _iterator2 = _createForOfIteratorHelper(range(tr.selection.to, Math.min(tr.selection.$to.end(), tr.doc.nodeSize - 2))),
              _step2;

          try {
            for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
              var _pos = _step2.value;
              afterMarkRange = getMarkRange(tr.doc.resolve(_pos), this.type);

              if (afterMarkRange) {
                break;
              }
            }
          } catch (err) {
            _iterator2.e(err);
          } finally {
            _iterator2.f();
          }

          if (afterMarkRange) {
            removeLink(afterMarkRange);
          }

          preserveSelection(selection, tr);
          var match = findMatchAtPosition(tr.doc.resolve(Math.min(range$1.from + 1, tr.doc.nodeSize - 2)), this.name);

          if (match) {
            updateLink({
              href: extractHref(match.text.full, this.options.defaultProtocol),
              auto: true
            }, match.range);
          } else {
            setMarkRemoved();
          }

          restore();
        }; // Respond when there is an exit.


        if (exitReason) {
          var isInvalid = isInvalidSplitReason(exitReason);
          var isRemoved = isRemovedReason(exitReason);

          if (isInvalid || isRemoved) {
            try {
              remove();
            } catch (_unused) {
              // Errors here can be ignored since they can be caused by deleting
              // the whole document.
              return;
            }
          }

          return;
        }

        var value;

        var _iterator3 = _createForOfIteratorHelper(range(range$1.from, range$1.to)),
            _step3;

        try {
          for (_iterator3.s(); !(_step3 = _iterator3.n()).done;) {
            var _value;

            var pos = _step3.value;
            value = getMarkRange(tr.doc.resolve(pos), this.type);

            if (((_value = value) === null || _value === void 0 ? void 0 : _value.mark.attrs.auto) === false) {
              return;
            }

            if (value) {
              break;
            }
          }
          /** Update the current range with the new link */

        } catch (err) {
          _iterator3.e(err);
        } finally {
          _iterator3.f();
        }

        function update() {
          custom(tr);
          updateLink({
            href,
            auto: true
          }, range$1);
          preserveSelection(selection, tr);
          restore();
        } // Update when there is a value


        if (!value) {
          update();
          return;
        } // Do nothing when the current mark is identical to the mark that would be created.


        if (value.from === range$1.from && value.to === range$1.to && value.mark.attrs.auto && value.mark.attrs.href === href) {
          return;
        }

        update();
      }
    };
    return [suggester];
  }
  /**
   * Track click events passed through to the editor.
   */


  createEventHandlers() {
    return {
      clickMark: (event, clickState) => {
        var markRange = clickState.getMark(this.type);

        if (!markRange) {
          return;
        }

        var attrs = markRange.mark.attrs;

        var data = _objectSpread(_objectSpread({}, attrs), markRange); // If one of the handlers returns `true` then return early.


        if (this.options.onClick(event, data)) {
          return true;
        }

        var handled = false;

        if (this.options.openLinkOnClick) {
          handled = true;
          var href = attrs.href;
          window.open(href, '_blank');
        }

        if (this.options.selectTextOnClick) {
          handled = true;
          this.store.commands.selectText(markRange);
        }

        return handled;
      }
    };
  }
  /**
   * The plugin for handling click events in the editor.
   *
   * TODO extract this into the events extension and move that extension into
   * core.
   */


  createPlugin() {
    return {
      props: {
        handleClick: (view, pos) => {
          if (!this.options.selectTextOnClick && !this.options.openLinkOnClick) {
            return false;
          }

          var _view$state = view.state,
              doc = _view$state.doc,
              tr = _view$state.tr;
          var range = getMarkRange(doc.resolve(pos), this.type);

          if (!range) {
            return false;
          }

          if (this.options.openLinkOnClick) {
            var href = range.mark.attrs.href;
            window.open(href, '_blank');
          }

          if (this.options.selectTextOnClick) {
            var $start = doc.resolve(range.from);
            var $end = doc.resolve(range.to);
            var transaction = tr.setSelection(new TextSelection($start, $end));
            view.dispatch(transaction);
          }

          return true;
        }
      },
      appendTransaction: (transactions, _oldState, state) => {
        var transactionsWithLinkMeta = transactions.filter(tr => !!tr.getMeta(this.name));

        if (transactionsWithLinkMeta.length === 0) {
          return;
        }

        transactionsWithLinkMeta.forEach(tr => {
          var trMeta = tr.getMeta(this.name);

          if (trMeta.command === UPDATE_LINK) {
            var range = trMeta.range,
                attrs = trMeta.attrs;
            var selection = state.selection,
                doc = state.doc;
            var _meta = {
              range,
              selection,
              doc,
              attrs
            };

            var _ref2 = range !== null && range !== void 0 ? range : selection,
                from = _ref2.from,
                to = _ref2.to;

            this.options.onUpdateLink(doc.textBetween(from, to), _meta);
          }
        });
      }
    };
  }

}, _temp)) || _class);
/**
 * Extract the `href` from the provided text.
 */

function extractHref(url, defaultProtocol) {
  return url.startsWith('http') || url.startsWith('//') ? url : "".concat(defaultProtocol, "//").concat(url);
}

export { LinkExtension };
